import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { api } from "@source/api/http";
import ReqError from "@source/api/reqError";
import {
  ConfirmConsentDocumentPdfRequest,
  ConsentCategory,
  ConsentCategoryListResponse,
  ConsentCategoryQuery,
  ConsentDocument,
  ConsentDocumentListResponse,
  ConsentDocumentPreview,
  ConsentDocumentQuery,
  ConsentPdfUploadTarget,
  ConsentTemplate,
  ConsentTemplateListResponse,
  ConsentTemplateQuery,
  CopyConsentTemplateRequest,
  CreateConsentCategoryRequest,
  CreateConsentDocumentRequest,
  CreateConsentTemplateRequest,
  ClinicalIdentityVerificationRequest,
  CloseWithdrawalRequest,
  CancelConsentDocumentRequest,
  EmployeeSignRequest,
  FamilyIdentityVerificationRequest,
  FamilySignRequest,
  ResidentConsentRequest,
  ResidentIdentityVerificationRequest,
  UpdateConsentCategoryRequest,
  UpdateConsentDocumentRequest,
  UpdateConsentTemplateRequest,
  WithdrawConsentRequest,
} from "@source/api/models/consentModels";
import {normalizeConsentPreviewContentType} from "@source/api/models/consentHelpers";
import {
  compactReportQuery,
  ConsentReportFile,
  ConsentReportQuery,
  consentReportFallbackName,
  fileNameFromContentDisposition,
} from "@source/api/models/consentReport";

interface ConsentErrorPayload {
  message?: string | string[];
  statusCode?: number;
  stringCode?: string;
}

const isolatedUploadClient = axios.create({
  withCredentials: false,
});

const readConsentErrorPayload = (data: unknown): ConsentErrorPayload | undefined => {
  if (!data) return undefined;
  if (typeof data === "object" && !(data instanceof ArrayBuffer) && !ArrayBuffer.isView(data)) {
    return data as ConsentErrorPayload;
  }
  const text = typeof data === "string"
    ? data
    : data instanceof ArrayBuffer
      ? new TextDecoder().decode(data)
      : ArrayBuffer.isView(data)
        ? new TextDecoder().decode(new Uint8Array(data.buffer, data.byteOffset, data.byteLength))
        : "";
  if (!text) return undefined;
  try {
    const parsed = JSON.parse(text) as ConsentErrorPayload;
    return parsed && typeof parsed === "object" ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const translateConsentError = (error: unknown): Error => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error : new Error("Unknown consent request error");
  }
  const axiosError = error as AxiosError<ConsentErrorPayload | ArrayBuffer>;
  const downError = ReqError.handleServerDownError(axiosError);
  if (downError) return downError;

  const payload = readConsentErrorPayload(axiosError.response?.data);
  const payloadMessage = payload?.message;
  const normalizedMessage = Array.isArray(payloadMessage)
    ? payloadMessage.join(". ")
    : typeof payloadMessage === "string" && payloadMessage.length > 0
      ? payloadMessage
      : "";
  const message = normalizedMessage || axiosError.message || "Consent request failed";
  return new ReqError(
    payload?.statusCode || axiosError.response?.status || 500,
    message,
    payload?.stringCode,
  );
};

const request = async <T>(callback: () => Promise<T>): Promise<T> => {
  try {
    return await callback();
  } catch (error: unknown) {
    throw translateConsentError(error);
  }
};

export type ConsentPdfUploadBody = Blob | ArrayBuffer | Uint8Array;

export default class ConsentService {
  static listCategories(organizationId: string, query: ConsentCategoryQuery = {}) {
    return request(async () =>
      (await api.get<ConsentCategoryListResponse>(
        `organizations/${organizationId}/consent-categories`,
        {params: query},
      )).data
    );
  }

  static getCategory(organizationId: string, categoryId: string) {
    return request(async () =>
      (await api.get<ConsentCategory>(
        `organizations/${organizationId}/consent-categories/${categoryId}`,
      )).data
    );
  }

  static createCategory(organizationId: string, body: CreateConsentCategoryRequest) {
    return request(async () =>
      (await api.post<ConsentCategory>(
        `organizations/${organizationId}/consent-categories`,
        body,
      )).data
    );
  }

  static updateCategory(
    organizationId: string,
    categoryId: string,
    body: UpdateConsentCategoryRequest,
  ) {
    return request(async () =>
      (await api.patch<ConsentCategory>(
        `organizations/${organizationId}/consent-categories/${categoryId}`,
        body,
      )).data
    );
  }

  static archiveCategory(organizationId: string, categoryId: string) {
    return request(async () =>
      (await api.post<ConsentCategory>(
        `organizations/${organizationId}/consent-categories/${categoryId}/archive`,
      )).data
    );
  }

  static createSupportingDocumentUploadUrl(organizationId: string, categoryId: string) {
    return request(async () =>
      (await api.post<ConsentPdfUploadTarget>(
        `organizations/${organizationId}/consent-categories/${categoryId}/supporting-documents/upload-url`,
      )).data
    );
  }

  static confirmSupportingDocument(
    organizationId: string,
    categoryId: string,
    body: ConfirmConsentDocumentPdfRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentCategory>(
        `organizations/${organizationId}/consent-categories/${categoryId}/supporting-documents/confirm`,
        body,
      )).data
    );
  }

  static deleteSupportingDocument(
    organizationId: string,
    categoryId: string,
    supportingDocumentId: string,
  ) {
    return request(async () =>
      (await api.delete<ConsentCategory>(
        `organizations/${organizationId}/consent-categories/${categoryId}/supporting-documents/${supportingDocumentId}`,
      )).data
    );
  }

  static listTemplates(organizationId: string, query: ConsentTemplateQuery = {}) {
    return request(async () =>
      (await api.get<ConsentTemplateListResponse>(
        `organizations/${organizationId}/consent-templates`,
        {params: query},
      )).data
    );
  }

  static getTemplate(organizationId: string, templateId: string) {
    return request(async () =>
      (await api.get<ConsentTemplate>(
        `organizations/${organizationId}/consent-templates/${templateId}`,
      )).data
    );
  }

  static createTemplate(organizationId: string, body: CreateConsentTemplateRequest) {
    return request(async () =>
      (await api.post<ConsentTemplate>(
        `organizations/${organizationId}/consent-templates`,
        body,
      )).data
    );
  }

  static updateTemplate(
    organizationId: string,
    templateId: string,
    body: UpdateConsentTemplateRequest,
  ) {
    return request(async () =>
      (await api.patch<ConsentTemplate>(
        `organizations/${organizationId}/consent-templates/${templateId}`,
        body,
      )).data
    );
  }

  static copyTemplate(
    organizationId: string,
    templateId: string,
    body: CopyConsentTemplateRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentTemplate>(
        `organizations/${organizationId}/consent-templates/${templateId}/copy`,
        body,
      )).data
    );
  }

  static archiveTemplate(organizationId: string, templateId: string) {
    return request(async () =>
      (await api.post<ConsentTemplate>(
        `organizations/${organizationId}/consent-templates/${templateId}/archive`,
      )).data
    );
  }

  static listDocuments(nursingHomeId: string, query: ConsentDocumentQuery = {}) {
    return request(async () =>
      (await api.get<ConsentDocumentListResponse>(
        `nursing-homes/${nursingHomeId}/consent-documents`,
        {params: query},
      )).data
    );
  }

  static getDocument(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.get<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}`,
      )).data
    );
  }

  static createDocument(nursingHomeId: string, body: CreateConsentDocumentRequest) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents`,
        body,
      )).data
    );
  }

  static updateDocument(
    nursingHomeId: string,
    documentId: string,
    body: UpdateConsentDocumentRequest,
  ) {
    return request(async () =>
      (await api.patch<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}`,
        body,
      )).data
    );
  }

  static deleteDocument(nursingHomeId: string, documentId: string) {
    return request(async () => {
      await api.delete(`nursing-homes/${nursingHomeId}/consent-documents/${documentId}`);
    });
  }

  static cancelDocument(nursingHomeId: string, documentId: string, body: CancelConsentDocumentRequest) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}/cancel`,
        body,
      )).data
    );
  }

  static sendDocument(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}/send`,
      )).data
    );
  }

  static remindFamilyMember(nursingHomeId: string, documentId: string, familyMemberId: string) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}/family-members/${familyMemberId}/remind`,
      )).data
    );
  }

  static remindEmployee(nursingHomeId: string, documentId: string, employeeId: string) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}/employees/${employeeId}/remind`,
      )).data
    );
  }

  static remindClinicalSigner(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}/clinical-signer/remind`,
      )).data
    );
  }

  static createPdfUploadUrl(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.post<ConsentPdfUploadTarget>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}/pdf/upload-url`,
      )).data
    );
  }

  /**
   * Uploads directly to the presigned URL through an isolated Axios instance.
   * The configured API base URL, Authorization header and interceptors are not used.
   */
  static uploadPdf(uploadUrl: string, body: ConsentPdfUploadBody): Promise<void> {
    return request(async () => {
      await isolatedUploadClient.put(uploadUrl, body, {
        headers: {"Content-Type": "application/pdf"},
      });
    });
  }

  static confirmPdf(
    nursingHomeId: string,
    documentId: string,
    body: ConfirmConsentDocumentPdfRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}/pdf/confirm`,
        body,
      )).data
    );
  }

  static getFinalPdf(documentId: string) {
    return request(async () =>
      (await api.get<ArrayBuffer>(
        `consent-documents/${documentId}/final-pdf`,
        {responseType: "arraybuffer"},
      )).data
    );
  }

  static getAuthorizedPreview(documentId: string) {
    return request(async () => {
      const response = await api.get<ArrayBuffer>(
        `consent-documents/${documentId}/preview`,
        {responseType: "arraybuffer"},
      );
      return {
        bytes: response.data,
        contentType: normalizeConsentPreviewContentType(response.headers["content-type"]),
      } as ConsentDocumentPreview;
    });
  }

  static listAwaitingEmployeeSignature() {
    return request(async () =>
      (await api.get<ConsentDocumentListResponse>(
        "consent-documents/employees/awaiting-signature",
      )).data
    );
  }

  static getEmployeeDocument(documentId: string) {
    return request(async () =>
      (await api.get<ConsentDocument>(
        `consent-documents/employees/${documentId}`,
      )).data
    );
  }

  static signEmployeeDocument(documentId: string, body: EmployeeSignRequest) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `consent-documents/employees/${documentId}/sign`,
        body,
      )).data
    );
  }

  static declineEmployeeDocument(documentId: string) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `consent-documents/employees/${documentId}/decline`,
      )).data
    );
  }

  static getResidentPresentation(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.get<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/resident-presentation/${documentId}`,
      )).data
    );
  }

  static verifyResidentIdentity(
    nursingHomeId: string,
    documentId: string,
    body: ResidentIdentityVerificationRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/resident-presentation/${documentId}/identity-verification`,
        body,
      )).data
    );
  }

  static recordResidentConsent(
    nursingHomeId: string,
    documentId: string,
    body: ResidentConsentRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/resident-presentation/${documentId}/consent`,
        body,
      )).data
    );
  }

  static recordResidentDecline(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/resident-presentation/${documentId}/decline`,
      )).data
    );
  }

  static getClinicalPresentation(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.get<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/clinical-presentation/${documentId}`,
      )).data
    );
  }

  static verifyClinicalIdentity(
    nursingHomeId: string,
    documentId: string,
    body: ClinicalIdentityVerificationRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/clinical-presentation/${documentId}/identity-verification`,
        body,
      )).data
    );
  }

  static recordClinicalConsent(
    nursingHomeId: string,
    documentId: string,
    body: EmployeeSignRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/clinical-presentation/${documentId}/sign`,
        body,
      )).data
    );
  }

  static withdrawDocument(nursingHomeId: string, documentId: string, body: WithdrawConsentRequest) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}/withdraw`,
        body,
      )).data
    );
  }

  static closeWithdrawalRequest(nursingHomeId: string, documentId: string, body: CloseWithdrawalRequest) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/${documentId}/withdrawal-request/close`,
        body,
      )).data
    );
  }

  static recordClinicalDecline(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/clinical-presentation/${documentId}/decline`,
      )).data
    );
  }

  static getFamilyPresentation(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.get<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/family-presentation/${documentId}`,
      )).data
    );
  }

  static verifyFamilyIdentity(
    nursingHomeId: string,
    documentId: string,
    body: FamilyIdentityVerificationRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/family-presentation/${documentId}/identity-verification`,
        body,
      )).data
    );
  }

  static recordFamilyConsent(
    nursingHomeId: string,
    documentId: string,
    body: FamilySignRequest,
  ) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/family-presentation/${documentId}/sign`,
        body,
      )).data
    );
  }

  static recordFamilyDecline(nursingHomeId: string, documentId: string) {
    return request(async () =>
      (await api.post<ConsentDocument>(
        `nursing-homes/${nursingHomeId}/consent-documents/family-presentation/${documentId}/decline`,
      )).data
    );
  }

  static downloadDocumentReport(query: ConsentReportQuery) {
    return request(() => downloadConsentReport("consent-reports/documents", query, "consent-documents-report"));
  }

  static downloadSignatureReport(query: ConsentReportQuery) {
    return request(() => downloadConsentReport("consent-reports/signatures", query, "consent-signatures-report"));
  }

  static downloadResidentReport(nursingHomeId: string, residentId: string, query: ConsentReportQuery = {}) {
    return request(() => downloadConsentReport(
      `nursing-homes/${nursingHomeId}/consent-documents/residents/${residentId}/report`,
      query,
      "resident-consent-report",
    ));
  }
}

const downloadConsentReport = async (
  url: string,
  query: ConsentReportQuery,
  fallbackPrefix: string,
): Promise<ConsentReportFile> => {
  const config: AxiosRequestConfig & { skipNursingHomeParam?: boolean } = {
    responseType: "arraybuffer",
    params: compactReportQuery(query),
    skipNursingHomeParam: true,
  };
  const response = await api.get<ArrayBuffer>(url, config);
  return {
    bytes: response.data,
    fileName: fileNameFromContentDisposition(
      response.headers["content-disposition"],
      consentReportFallbackName(fallbackPrefix),
    ),
  };
};
